import React, { useRef, useState } from 'react';
import { CalendarIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ArrowDownSLineIconV7 } from '../ui/figma-icons-v7';
import { MultiSelectDropdown } from '../filters/MultiSelectDropdown';
import DateRangePicker from './DateRangePicker';
import type {
  CandidateSourceFilter,
  CandidateStatusFilter,
  DialogBusinessStatus,
  DialogLifecycleStatus,
  MessengerType,
} from '../../types/aiRecruiter';
import { CANDIDATE_STATUS_OPTIONS } from './utils/candidateStatusFilter';

export interface DialogFiltersState {
  dialogStatuses: DialogBusinessStatus[];
  sources: CandidateSourceFilter[];
  messengers: MessengerType[];
  lifecycleStatuses: DialogLifecycleStatus[];
  candidateStatuses: CandidateStatusFilter[];
  createdFrom: Date | null;
  createdTo: Date | null;
}

export const emptyDialogFilters: DialogFiltersState = {
  dialogStatuses: [],
  sources: [],
  messengers: [],
  lifecycleStatuses: [],
  candidateStatuses: [],
  createdFrom: null,
  createdTo: null,
};

interface Props {
  value: DialogFiltersState;
  onChange: (next: DialogFiltersState) => void;
}

type DialogStatusOptionValue = DialogBusinessStatus | DialogLifecycleStatus;

const DIALOG_STATUS_OPTIONS = [
  { value: 'WITH_REPLY' as const, label: 'С ответом' },
  { value: 'NO_REPLY' as const, label: 'Без ответа' },
  { value: 'OVERDUE' as const, label: 'Просрочен' },
  { value: 'PROBLEM' as const, label: 'Проблема' },
  { value: 'STOPPED' as const, label: 'Остановлен' },
  { value: 'ACTIVE' as const, label: 'Активен' },
];

const SOURCE_OPTIONS = [
  { value: 'HH_RESPONSE' as const, label: 'Отклики hh' },
  { value: 'HH_SEARCH' as const, label: 'Поиск hh' },
  { value: 'ESTAFF' as const, label: 'E-Staff' },
  { value: 'AVITO_RESPONSE' as const, label: 'Отклики Авито' },
  { value: 'AVITO_SEARCH' as const, label: 'Поиск Авито' },
];

const MESSENGER_OPTIONS = [
  { value: 'HH' as const, label: 'hh' },
  { value: 'TELEGRAM' as const, label: 'Telegram' },
  { value: 'WHATSAPP' as const, label: 'WhatsApp' },
  { value: 'MAX' as const, label: 'Max' },
  { value: 'AVITO' as const, label: 'Авито' },
];

const formatDate = (d: Date | null): string | null =>
  d
    ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
    : null;

export const DialogFiltersBar: React.FC<Props> = ({ value, onChange }) => {
  const [isCalendarOpen, setCalendarOpen] = useState(false);
  const calendarWrapRef = useRef<HTMLDivElement>(null);

  const dateLabel = (() => {
    const from = formatDate(value.createdFrom);
    const to = formatDate(value.createdTo);
    if (from && to) return `${from} — ${to}`;
    if (from) return `с ${from}`;
    if (to) return `до ${to}`;
    return 'DD / MM / YYYY';
  })();

  const hasDateValue = value.createdFrom || value.createdTo;
  const selectedDialogStatusOptions: DialogStatusOptionValue[] = [
    ...value.dialogStatuses,
    ...value.lifecycleStatuses,
  ];

  return (
    <div className="flex flex-row items-center gap-2 w-full flex-wrap">
      <div>
        <MultiSelectDropdown<DialogStatusOptionValue>
          placeholder="Статус диалога"
          value={selectedDialogStatusOptions}
          onChange={(next) => onChange({
            ...value,
            dialogStatuses: next.filter((v): v is DialogBusinessStatus =>
              v === 'WITH_REPLY' || v === 'NO_REPLY' || v === 'OVERDUE' || v === 'PROBLEM'),
            lifecycleStatuses: next.filter((v): v is DialogLifecycleStatus =>
              v === 'ACTIVE' || v === 'STOPPED'),
          })}
          options={DIALOG_STATUS_OPTIONS}
        />
      </div>

      <div>
        <MultiSelectDropdown<CandidateStatusFilter>
          placeholder="Статус кандидата"
          value={value.candidateStatuses}
          onChange={(next) => onChange({ ...value, candidateStatuses: next })}
          options={CANDIDATE_STATUS_OPTIONS}
        />
      </div>

      <div className="w-[164px]">
        <MultiSelectDropdown<CandidateSourceFilter>
          placeholder="Источник"
          value={value.sources}
          onChange={(next) => onChange({ ...value, sources: next })}
          options={SOURCE_OPTIONS}
        />
      </div>

      <div className="w-[164px]">
        <MultiSelectDropdown<MessengerType>
          placeholder="Мессенджер"
          value={value.messengers}
          onChange={(next) => onChange({ ...value, messengers: next })}
          options={MESSENGER_OPTIONS}
        />
      </div>

      {/*
        Диапазон периода создания диалога. Кнопка оформлена как pill-фильтр
        (Figma node 8668:29164): белый фон, border #E2E4E9 12px, иконка календаря
        20px слева, плейсхолдер "DD / MM / YYYY" цветом #868C98.
      */}
      <div ref={calendarWrapRef} className="relative w-[260px]">
        <button
          type="button"
          onClick={() => setCalendarOpen((v) => !v)}
          className="bg-white box-border flex flex-row gap-2 items-center justify-start pl-3 pr-[10px] py-[10px] rounded-[12px] border border-[#E2E4E9] shadow-[0px_1px_2px_0px_rgba(228,229,231,0.24)] hover:bg-[#F6F8FA] transition-colors cursor-pointer w-full h-10"
        >
          <CalendarIcon className="w-5 h-5 shrink-0 text-[#525866]" />
          <span
            className={`flex-1 text-left font-['Inter:Regular',_sans-serif] font-normal text-[14px] leading-[20px] truncate ${
              hasDateValue ? 'text-[#0A0D14]' : 'text-[#868C98]'
            }`}
          >
            {dateLabel}
          </span>
          {hasDateValue ? (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onChange({ ...value, createdFrom: null, createdTo: null });
                setCalendarOpen(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange({ ...value, createdFrom: null, createdTo: null });
                  setCalendarOpen(false);
                }
              }}
              className="w-5 h-5 shrink-0 flex items-center justify-center rounded hover:bg-[#E2E4E9] text-[#525866] transition-colors cursor-pointer"
              aria-label="Сбросить период"
            >
              <XMarkIcon className="w-4 h-4" />
            </span>
          ) : (
            <ArrowDownSLineIconV7
              size={20}
              className={`w-5 h-5 shrink-0 transition-transform ${isCalendarOpen ? 'rotate-180' : ''}`}
            />
          )}
        </button>

        <DateRangePicker
          isOpen={isCalendarOpen}
          onClose={() => setCalendarOpen(false)}
          selectedRange={{ from: value.createdFrom, to: value.createdTo }}
          onSelect={(range) =>
            onChange({ ...value, createdFrom: range.from, createdTo: range.to })
          }
        />
      </div>
    </div>
  );
};

export default DialogFiltersBar;
